import { canViewInsuranceCampaigns } from '@/general/src/auth/can.js'
import { getInsuranceCustomerTypeTitle } from '@/general/src/insuranceCustomerType/shared.js'
import { getInsuranceScenarioResultColumns } from '@/general/src/insuranceScenario/webapp.js'
// import type { InsuranceScenarioStoreDataWithoutIssue } from '@/general/src/insuranceScenario/desktopapp.shared.utils.js'
import type { ClientInsuranceCampaignForAdmin } from '@/general/src/insuranceCampaign/backend.js'
import { getInsuranceScenarioStatusTitle, getInsuranceScenarioTitle } from '@/general/src/insuranceScenario/shared.js'
import { getInsuranceTypeTitle } from '@/general/src/insuranceType/shared.js'
import { copyToClipboard } from '@/general/src/other/copyToClipboard.js'
import { GeneralLayout } from '@/webapp/src/components/layout/GeneralLayout/index.js'
import { Checkboxesy, useFormy } from '@/webapp/src/lib/formy.js'
import { withPageWrapper } from '@/webapp/src/lib/pageWrapper.js'
import { adminAgentViewRoute, adminInsuranceCampaignViewRoute } from '@/webapp/src/lib/routes.js'
import { toast } from '@/webapp/src/lib/toaster.js'
import { trpc } from '@/webapp/src/lib/trpc.js'
import {
  Block,
  Button,
  Buttons,
  FormItems,
  LabeledValues,
  Link,
  Segment,
  Segments,
  Table,
} from '@/webapp/src/lib/uninty.components.js'
import { useRouteParams } from '@/webapp/src/lib/useRoute.js'
import { formatDate } from 'date-fns'
import { useMemo } from 'react'
import { pick } from 'svag-utils'

const Source = ({ insuranceCampaign }: { insuranceCampaign: ClientInsuranceCampaignForAdmin }) => {
  const formy = useFormy({
    initialValues: {
      insuranceScenariosKeys: insuranceCampaign.insuranceScenariosInfos.map((insuranceScenariosInfo) => {
        return insuranceScenariosInfo.insuranceScenarioKey
      }),
    },
    // validationSchema: z.object({
    //   insuranceScenariosKeys: z.array(z.string()),
    // }),
  })
  const source = useMemo(() => {
    const sourceObject = {
      ...pick(insuranceCampaign, ['input', 'insuranceType', 'insuranceCustomerType', 'insuranceProviders']),
      insuranceScenariosKeys: formy.values.insuranceScenariosKeys,
    }
    return JSON.stringify(sourceObject)
  }, [formy.values.insuranceScenariosKeys, insuranceCampaign.id])
  return (
    <Segment
      title="Исходные данные"
      desc="Для разработчиков удобно, чтобы вы заводя задачу на проавку сценария, прикладывали этот исходный код с нужными выбранными сценариями, скорее всего с 1 единственным сценарием, который нужно править"
      size="m"
    >
      <FormItems style={{ width: '100%', position: 'relative' }}>
        <Checkboxesy
          label="Сценарии"
          {...formy.getFieldProps('insuranceScenariosKeys')}
          options={insuranceCampaign.insuranceScenariosInfos.map((insuranceScenariosInfo) => {
            return {
              label: getInsuranceScenarioTitle({ insuranceScenarioKey: insuranceScenariosInfo.insuranceScenarioKey }),
              value: insuranceScenariosInfo.insuranceScenarioKey,
            }
          })}
        />
        <code style={{ overflowX: 'auto', maxWidth: '500px', padding: '10px', border: '1px solid #ccc' }}>
          <pre>{source}</pre>
        </code>
        <Buttons>
          <Button
            onClick={() => {
              copyToClipboard(source)
              void toast.success('Скопировано')
            }}
            type="button"
          >
            Скопировать
          </Button>
        </Buttons>
      </FormItems>
    </Segment>
  )
}

const Errors = ({ insuranceCampaign }: { insuranceCampaign: ClientInsuranceCampaignForAdmin }) => {
  const formy = useFormy({
    initialValues: {
      insuranceScenariosKeys: insuranceCampaign.insuranceScenariosInfos.map((insuranceScenariosInfo) => {
        return insuranceScenariosInfo.insuranceScenarioKey
      }),
    },
    // validationSchema: z.object({
    //   insuranceScenariosKeys: z.array(z.string()),
    // }),
  })
  const records = useMemo(() => {
    return insuranceCampaign.insuranceScenariosInfos
      .filter((info) => {
        return formy.values.insuranceScenariosKeys.includes(info.insuranceScenarioKey)
      })
      .flatMap((info) => info.wasIssues.map((wi) => ({ ...wi, insuranceScenarioKey: info.insuranceScenarioKey })))
  }, [insuranceCampaign.insuranceScenariosInfos, formy.values.insuranceScenariosKeys])
  return (
    <Segment title={`Ошибки`} size="m" style={{ width: '100%' }}>
      <FormItems>
        <Checkboxesy
          label="Сценарии"
          {...formy.getFieldProps('insuranceScenariosKeys')}
          options={insuranceCampaign.insuranceScenariosInfos.map((insuranceScenariosInfo) => {
            return {
              label: getInsuranceScenarioTitle({ insuranceScenarioKey: insuranceScenariosInfo.insuranceScenarioKey }),
              value: insuranceScenariosInfo.insuranceScenarioKey,
            }
          })}
        />
        <Table
          columns={[
            {
              heading: 'Ожидаемо',
              width: 100,
              key: 'expected',
              body: (record) => (record.expected ? 'Да' : 'Нет'),
            },
            {
              heading: 'Сценарий',
              width: 250,
              key: 'insuranceScenarioKey',
              body: (record) => getInsuranceScenarioTitle({ insuranceScenarioKey: record.insuranceScenarioKey }),
            },
            {
              heading: 'Шаг',
              width: 400,
              key: 'step',
              body: (record) => (typeof record.step === 'string' ? record.step : record.step.userText),
            },
            {
              heading: 'Сообщение',
              width: 800,
              key: 'wasIssues',
              body: (record) => record.message,
            },
            {
              heading: 'Ошибка',
              width: 800,
              key: 'cause',
              body: (record) => record.cause,
            },
          ]}
          records={records}
        />
      </FormItems>
    </Segment>
  )
}

export const AdminInsuranceCampaignViewPage = withPageWrapper({
  isTitleExact: true,
  title: 'Страховка',
  Layout: GeneralLayout,
  authorizedAdminsOnly: true,
  checkAccess: ({ ctx }) => canViewInsuranceCampaigns(ctx.me.admin),
  useQuery: () => {
    const { routeParams } = useRouteParams(adminInsuranceCampaignViewRoute)
    return trpc.getInsuranceCampaignForAdmin.useQuery({
      insuranceCampaignSn: +routeParams.insuranceCampaignSn,
    })
  },
  setProps: ({ queryResult }) => ({
    insuranceCampaign: queryResult.data.insuranceCampaign,
  }),
})(({ insuranceCampaign }) => {
  const columns = getInsuranceScenarioResultColumns({ insuranceType: insuranceCampaign.insuranceType })
  return (
    <Block fcnw>
      <Segments>
        <Segment title={`Страховка #${insuranceCampaign.sn}`} size="m">
          <FormItems>
            <LabeledValues
              items={[
                { label: 'SN', value: insuranceCampaign.sn },
                {
                  label: 'Агент',
                  value: (
                    <Link href={adminAgentViewRoute.get({ agentSn: insuranceCampaign.agent.sn })}>
                      {insuranceCampaign.agent.sn}
                    </Link>
                  ),
                },
                { label: 'Клиент', value: insuranceCampaign.clientName },
                { label: 'Тип клиента', value: getInsuranceCustomerTypeTitle(insuranceCampaign.insuranceCustomerType) },
                { label: 'Тип страховки', value: getInsuranceTypeTitle(insuranceCampaign.insuranceType) },
                { label: 'Дата создания', value: formatDate(insuranceCampaign.createdAt, 'dd.MM.yyyy HH:mm') },
              ]}
            />
          </FormItems>
        </Segment>
        <Segment title={`Результаты`} size="m" style={{ width: '100%' }}>
          <Table
            columns={[
              {
                heading: 'Сценарий',
                width: 200,
                key: 'insuranceScenarioKey',
                body: (record) =>
                  getInsuranceScenarioTitle({
                    insuranceScenarioKey: record.insuranceScenarioKey,
                  }),
              },
              {
                heading: 'Статус',
                width: 200,
                key: 'status',
                body: (record) => getInsuranceScenarioStatusTitle({ status: record.status }),
              },
              ...columns,
            ]}
            records={insuranceCampaign.insuranceScenariosInfos}
          />
        </Segment>
        <Source insuranceCampaign={insuranceCampaign} />
        <Errors insuranceCampaign={insuranceCampaign} />
      </Segments>
    </Block>
  )
})
